import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronLeft, ChevronRight, History, Home, Search, ShieldCheck, Trophy, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import { DrawDetailContent } from '../../features/archive/DrawDetailContent';
import { fetchArchiveRounds, fetchRoundByNumber, drawResolvedTs, type ArchiveRound } from '../../features/archive/api';
import { GAME_LABELS, gameNameKey } from '../../shared/game/betOptions';
import { roundSeqFromRound, roundDateFromRound } from '../../shared/game/roundLabel';
import { brDayLabel, localTimeWithTz } from '../../shared/lib/datetime';
import { GameIcon } from '../../shared/game/GameIcon';
import { useI18nStore } from '../../shared/i18n/i18nStore';

type ArchiveGameFilter = 'all' | 'lastdigit' | 'sicbo' | 'roulette';

const GAME_FILTERS: ArchiveGameFilter[] = ['all', 'lastdigit', 'sicbo', 'roulette'];

export function ArchivePage() {
  const setRoute = useAppStore((state) => state.setRoute);
  const showToast = useAppStore((state) => state.showToast);
  const activeArchiveRoundNumber = useAppStore((state) => state.activeArchiveRoundNumber);
  const setActiveArchiveRoundNumber = useAppStore((state) => state.setActiveArchiveRoundNumber);
  const archiveReturn = useAppStore((state) => state.archiveReturn);
  const setArchiveReturn = useAppStore((state) => state.setArchiveReturn);
  const reopenRaffleSheetAt = useAppStore((state) => state.reopenRaffleSheetAt);
  const t = useI18nStore((state) => state.t);
  const [dayOffset, setDayOffset] = useState(0);
  const [filter, setFilter] = useState<ArchiveGameFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedRound, setSelectedRound] = useState<ArchiveRound | null>(null);
  // Archive is grouped by the USER's LOCAL day (so "Today" = your today). A local
  // day spans ≤2 UTC days on the backend, so fetch both and keep the rounds whose
  // local date matches. The round # stays Brazil-anchored (canonical) and each
  // card is tagged with its Brazil draw day, which changes once mid-list.
  const localDayStr = localDay(dayOffset);
  const utcDates = utcDatesForLocalDay(localDayStr);
  const query = useQuery({
    queryKey: ['archive-rounds-local', localDayStr],
    queryFn: async () => {
      const results = await Promise.all(utcDates.map((utc) => fetchArchiveRounds(utc, 288)));
      return { rounds: results.flatMap((result) => result.rounds || []) };
    },
  });
  const allRounds = useMemo(
    () => normalizeArchiveRounds(query.data?.rounds || [])
      .filter((round) => localDateOf(round) === localDayStr)
      .sort((a, b) => Number(b.cutoff_ts || b.starts_at || 0) - Number(a.cutoff_ts || a.starts_at || 0)),
    [query.data?.rounds, localDayStr],
  );
  const rounds = useMemo(
    () => allRounds.filter((round) => matchesSearch(round, search)),
    [allRounds, search],
  );

  // A deep-linked round (e.g. a raffle's anchor round) is opened straight from
  // the loaded day when present. Otherwise it's fetched directly by global
  // number, so verifying a round from any other day still lands on its detail.
  const inDayMatch = useMemo(
    () => (activeArchiveRoundNumber && allRounds.length ? findArchiveRound(allRounds, activeArchiveRoundNumber) : null),
    [activeArchiveRoundNumber, allRounds],
  );
  const remoteRoundNumber = useMemo(() => {
    if (!activeArchiveRoundNumber || inDayMatch) return null;
    const n = Number(String(activeArchiveRoundNumber).replace(/[^0-9]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [activeArchiveRoundNumber, inDayMatch]);
  const remoteRoundQuery = useQuery({
    queryKey: ['archive-round-by-number', remoteRoundNumber],
    queryFn: () => fetchRoundByNumber(remoteRoundNumber as number),
    enabled: remoteRoundNumber != null,
    retry: false,
  });

  useEffect(() => {
    if (!activeArchiveRoundNumber) return;
    const match = inDayMatch
      || (remoteRoundQuery.data && Number(remoteRoundQuery.data.round_number) > 0 ? remoteRoundQuery.data : null);
    if (!match) return;
    setSelectedRound((current) => (current && archiveRoundRouteId(current) === archiveRoundRouteId(match) ? current : match));
  }, [activeArchiveRoundNumber, inDayMatch, remoteRoundQuery.data]);

  useEffect(() => {
    if (remoteRoundNumber != null && remoteRoundQuery.isError) {
      showToast(t('archiveRoundNotFound'), 'warn');
      setActiveArchiveRoundNumber(null, { replace: true });
      // The verify excursion died before its detail could open — a later,
      // unrelated round-close must not teleport back to the result sheet.
      if (useAppStore.getState().archiveReturn) setArchiveReturn(null);
    }
  }, [remoteRoundNumber, remoteRoundQuery.isError, showToast, t, setActiveArchiveRoundNumber, setArchiveReturn]);

  function openRound(round: ArchiveRound) {
    setSelectedRound(round);
    setActiveArchiveRoundNumber(archiveRoundRouteId(round));
  }

  function closeRound() {
    setSelectedRound(null);
    // Arrived via "Verify this round" on a raffle result → closing the detail
    // ends the excursion: go back to the result sheet over its original page.
    if (archiveReturn) {
      const ret = archiveReturn;
      setArchiveReturn(null);
      reopenRaffleSheetAt(ret.origin, 'result', ret.resultId);
      return; // leaving the archive route clears the deep-linked round number
    }
    setActiveArchiveRoundNumber(null, { replace: true });
  }

  // A verify-return that can no longer be consumed (deep-link failed, or the
  // user leaves for the lobby instead of closing the detail) must not teleport
  // a later, unrelated round-close — drop it on those explicit exits. (Not an
  // unmount cleanup: StrictMode's mount→unmount→mount would wipe it on arrival.)
  function leaveToLobby() {
    if (archiveReturn) setArchiveReturn(null);
    setRoute('lobby');
  }

  function openSearchMatch() {
    const value = search.trim();
    if (!value) return;
    const match = findArchiveRound(allRounds, value);
    if (match) openRound(match);
    else showToast(t('archiveRoundNotFound'), 'warn');
  }

  return (
    <section className="tab-page archive-page">
      <header className="subroute-header">
        <button className="icon-button" type="button" onClick={leaveToLobby} aria-label={t('backToLobby')}>
          <ArrowLeft size={19} aria-hidden="true" />
        </button>
        <div>
          <h1>{t('archiveTitle')}</h1>
          <p className="archive-tap-hint">{t('archiveTapHint')}</p>
        </div>
        <button className="icon-button" type="button" onClick={() => setRoute('rules')} aria-label={t('rulesHeader')}>
          <ShieldCheck size={18} aria-hidden="true" />
        </button>
      </header>

      <section className="archive-toolbar">
        <label className="archive-search">
          <Search size={15} aria-hidden="true" />
          <input
            value={search}
            inputMode="numeric"
            placeholder={t('archiveSearchPlaceholder')}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') openSearchMatch();
            }}
          />
          {search ? (
            <button type="button" onClick={() => setSearch('')} aria-label={t('clear')}>
              <X size={14} aria-hidden="true" />
            </button>
          ) : null}
        </label>
        <div className="archive-day-switch">
          <button type="button" disabled={dayOffset <= -7} onClick={() => setDayOffset((value) => Math.max(-7, value - 1))}>
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <div>
            <strong>{dayOffset === 0 ? t('drawsToday') : dayOffset === -1 ? t('archiveYesterday') : brDayLabel(localDayStr)}</strong>
            <span>{brDayLabel(localDayStr)}</span>
          </div>
          <button type="button" disabled={dayOffset >= 0} onClick={() => setDayOffset((value) => Math.min(0, value + 1))}>
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
        <nav className="archive-filter-row" aria-label={t('filterRooms')}>
          {GAME_FILTERS.map((item) => (
            <button className={filter === item ? 'active' : ''} type="button" key={item} onClick={() => setFilter(item)}>
              {item === 'all' ? t('archiveAll') : (
                <>
                  <span className="archive-chip-icon"><GameIcon game={item} /></span>
                  {t(gameNameKey(item))}
                </>
              )}
            </button>
          ))}
        </nav>
      </section>

      <section className="archive-round-list">
        {query.isLoading ? <ArchiveState title={t('loading')} /> : null}
        {query.isError ? <ArchiveState title={t('roomLoadError')} body={t('roomLoadErrorBody')} actionLabel={t('retry')} onAction={() => query.refetch()} /> : null}
        {!query.isLoading && !query.isError && rounds.length === 0 ? <ArchiveState title={t('archiveEmpty')} /> : null}
        <div className="archive-grid">
          {rounds.map((round) => (
            <ArchiveRoundCard key={round.round_number} round={round} filter={filter} onClick={() => openRound(round)} />
          ))}
        </div>
      </section>

      {selectedRound ? <RoundDetailSheet round={selectedRound} onClose={closeRound} /> : null}
    </section>
  );
}

function ArchiveRoundCard({ round, filter, onClick }: { round: ArchiveRound; filter: ArchiveGameFilter; onClick: () => void }) {
  const t = useI18nStore((state) => state.t);
  const games = round.games || {};
  const dice = games.sicbo || [];
  const roulette = Number(games.roulette ?? -1);
  return (
    <button className="archive-round-card" type="button" onClick={onClick}>
      <header>
        <strong>#{dailyRoundSeq(round).toString().padStart(3, '0')}</strong>
        <span>{formatArchiveTime(drawResolvedTs(round))}</span>
      </header>
      {(filter === 'all' || filter === 'lastdigit') ? (
        <div className="archive-result-row">
          <span className="archive-game-glyph"><GameIcon game="lastdigit" /></span>
          <b className={Number(games.lastdigit ?? 0) >= 5 ? 'text-rose-400' : 'text-teal-400'}>{games.lastdigit ?? '-'}</b>
          <em className={Number(games.lastdigit ?? 0) >= 5 ? 'text-rose-400' : 'text-teal-400'}>{Number(games.lastdigit ?? 0) >= 5 ? t('big') : t('small')}</em>
        </div>
      ) : null}
      {(filter === 'all' || filter === 'sicbo') ? (
        <div className="archive-result-row">
          <span className="archive-game-glyph"><GameIcon game="sicbo" /></span>
          <b className={`font-mono ${sicboTone(dice)}`}>{dice.length ? `${dice.join('+')}=${dice.reduce((sum, item) => sum + Number(item || 0), 0)}` : '—'}</b>
        </div>
      ) : null}
      {(filter === 'all' || filter === 'roulette') ? (
        <div className="archive-result-row">
          <span className="archive-game-glyph"><GameIcon game="roulette" /></span>
          <b className={`roulette-pill-react pill-${rouletteColor(roulette)}`}>{roulette >= 0 ? roulette : '—'}</b>
          <em className={roulette >= 0 ? (rouletteColor(roulette) === 'red' ? 'text-rose-400' : rouletteColor(roulette) === 'black' ? 'text-slate-300' : 'text-emerald-400') : ''}>{roulette >= 0 ? t(rouletteColor(roulette)) : '-'}</em>
        </div>
      ) : null}
    </button>
  );
}

function RoundDetailSheet({ round, onClose }: { round: ArchiveRound; onClose: () => void }) {
  const t = useI18nStore((state) => state.t);
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet archive-detail-sheet" role="dialog" aria-modal="true" aria-label={t('archiveDetailTitle')} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>#{String(dailyRoundSeq(round)).padStart(3, '0')}</h2>
            <p>{t('roundDrawDay')} {brDayLabel(roundDateFromRound(round))} · {t('roundGlobalNumber').replace('%n%', String(round.round_number))}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>

        <DrawDetailContent round={round} />
      </section>
    </div>
  );
}

function ArchiveState({ title, body, actionLabel, onAction }: { title: string; body?: string; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="archive-state">
      <History size={28} aria-hidden="true" />
      <strong>{title}</strong>
      {body ? <p>{body}</p> : null}
      {actionLabel && onAction ? <button type="button" onClick={onAction}>{actionLabel}</button> : null}
    </div>
  );
}

function normalizeArchiveRounds(rounds: ArchiveRound[]) {
  return rounds
    .filter((round) => round.games && round.status !== 'VOID')
    .sort((a, b) => Number(b.cutoff_ts || b.starts_at || 0) - Number(a.cutoff_ts || a.starts_at || 0));
}

function matchesSearch(round: ArchiveRound, search: string) {
  // Legacy: typed number filters by EXACT daily sequence of the visible day.
  const value = search.trim().replace(/^#/, '');
  if (!value) return true;
  return String(dailyRoundSeq(round)) === String(Number(value)) || String(round.round_number) === value;
}

function findArchiveRound(rounds: ArchiveRound[], value: string) {
  const target = value.trim().replace(/^#/, '');
  if (!target) return null;
  return rounds.find((round) => archiveRoundRouteId(round) === target) ||
    rounds.find((round) => String(dailyRoundSeq(round)).padStart(3, '0') === target.padStart(3, '0')) ||
    rounds.find((round) => matchesSearch(round, target)) ||
    null;
}

function archiveRoundRouteId(round: ArchiveRound) {
  return String(round.round_number || '').trim();
}

function pad2(value: number) {
  return String(value).padStart(2, '0');
}

// The viewer's LOCAL calendar day, offset days from today. "2026-07-12".
function localDay(offset: number): string {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

// The UTC calendar dates the backend must return to fully cover one local day
// (a local day straddles at most two UTC days).
function utcDatesForLocalDay(localDayStr: string): string[] {
  const [year, month, day] = localDayStr.split('-').map(Number);
  const startUtc = new Date(year, month - 1, day, 0, 0, 0).toISOString().slice(0, 10);
  const endUtc = new Date(year, month - 1, day, 23, 59, 59).toISOString().slice(0, 10);
  return startUtc === endUtc ? [startUtc] : [startUtc, endUtc];
}

// A round's LOCAL calendar date, for grouping it under the user's day.
function localDateOf(round: ArchiveRound): string {
  const ts = Number(round.cutoff_ts || round.starts_at || 0);
  if (!ts) return '';
  const date = new Date(ts * 1000);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function dailyRoundSeq(round: ArchiveRound) {
  // Canonical daily sequence: Brazil UTC-3, 0-based — identical to the room and
  // My Bets. (Previously computed in the viewer's LOCAL timezone + 1-based, so
  // the same draw showed a different number in every timezone.)
  const seq = roundSeqFromRound(round);
  return seq !== '' ? Number(seq) : Number(round.round_number || 0);
}

function formatArchiveTime(seconds: number) {
  return localTimeWithTz(seconds);
}

function rouletteColor(value: number): 'red' | 'black' | 'green' {
  if (value === 0) return 'green';
  return new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]).has(value) ? 'red' : 'black';
}

function sicboTone(dice: (number | string)[]) {
  if (!dice.length) return '';
  const nums = dice.map((d) => Number(d || 0));
  if (nums[0] === nums[1] && nums[1] === nums[2]) return 'text-gold-400';
  const sum = nums.reduce((acc, n) => acc + n, 0);
  return sum >= 11 ? 'text-rose-400' : 'text-teal-400';
}
